import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  User, Briefcase, GraduationCap, Star, Globe, Award, ChevronRight,
  ChevronLeft, ChevronDown, Wand2, Loader2, Download, CheckCircle, Upload, Plus, X, Trash2, ShieldCheck
} from "lucide-react";
import { useAuth, getAuthHeaders } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription } from "../components/ui/drawer";
import { track } from "../lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY_EXP = { company: "", position: "", start_date: "", end_date: "", current: false, description: "" };
const EMPTY_EDU = { institution: "", degree: "", field: "", year: "" };

function FieldLabel({ children }) {
  return <label style={{ display: "block", fontSize: "12px", fontWeight: 600, color: "#64748B", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "6px", fontFamily: "Poppins, sans-serif" }}>{children}</label>;
}
function Input({ value, onChange, placeholder, type = "text", ...props }) {
  return (
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      style={{ width: "100%", height: "42px", border: "1px solid #E5E7EB", borderRadius: "10px", padding: "0 14px", fontSize: "16px", fontFamily: "Poppins, sans-serif", color: "#0F172A", outline: "none", boxSizing: "border-box" }}
      {...props}
    />
  );
}
function Textarea({ value, onChange, placeholder, rows = 4 }) {
  return (
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      rows={rows}
      style={{ width: "100%", border: "1px solid #E5E7EB", borderRadius: "10px", padding: "12px 14px", fontSize: "16px", fontFamily: "Poppins, sans-serif", color: "#0F172A", outline: "none", resize: "vertical", boxSizing: "border-box" }}
    />
  );
}

function AIButton({ onClick, loading, label = "AI Improve" }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={loading}
      style={{ display: "inline-flex", alignItems: "center", gap: "6px", height: "36px", padding: "0 14px", background: "linear-gradient(135deg, #028CF8, #0270C5)", color: "white", borderRadius: "8px", fontSize: "12px", fontWeight: 600, fontFamily: "Poppins, sans-serif", border: "none", cursor: "pointer", opacity: loading ? 0.7 : 1 }}
    >
      {loading ? <Loader2 style={{ width: 13, height: 13, animation: "spin 1s linear infinite" }} /> : <Wand2 style={{ width: 13, height: 13 }} />}
      {label}
    </button>
  );
}

function CVScoreRing({ score, label }) {
  const color = score >= 80 ? "#10B981" : score >= 60 ? "#028CF8" : "#64748B";
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "4px" }}>
      <div style={{ width: 80, height: 80, borderRadius: "50%", border: `6px solid ${color}`, display: "flex", alignItems: "center", justifyContent: "center", background: `${color}10` }}>
        <span style={{ fontSize: "22px", fontWeight: 700, color, fontFamily: "Poppins, sans-serif" }}>{score}</span>
      </div>
      <span style={{ fontSize: "11px", color: "#64748B", fontFamily: "Poppins, sans-serif", fontWeight: 600 }}>{label}</span>
    </div>
  );
}

export default function CVBuilderPage() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();

  const STEPS = [
    { id: "personal",   label: t("cv.stepPersonal"),   icon: User },
    { id: "summary",    label: t("cv.stepSummary"),    icon: Star },
    { id: "experience", label: t("cv.stepExperience"), icon: Briefcase },
    { id: "education",  label: t("cv.stepEducation"),  icon: GraduationCap },
    { id: "skills",     label: t("cv.stepSkills"),     icon: Star },
    { id: "languages",  label: t("cv.stepLanguages"),  icon: Globe },
    { id: "preview",    label: t("cv.stepPreview"),    icon: CheckCircle },
  ];
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [savedCV, setSavedCV] = useState(null);
  const [toast, setToast] = useState("");
  const [drawerOpen, setDrawerOpen] = useState(false);

  const [cv, setCv] = useState({
    name: user?.name || "", title: "", location: "", phone: "", email: user?.email || "", linkedin: "",
    summary: "", experience: [{ ...EMPTY_EXP }], education: [{ ...EMPTY_EDU }],
    skills: [], languages: [], certifications: [], industries: [], years_experience: 0,
  });
  const [hydrating, setHydrating] = useState(true);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(""), 3000); };
  const set = (key, val) => setCv((c) => ({ ...c, [key]: val }));

  // Hydrate from the saved CV (same GET /api/cv the Professional Profile tab reads) so
  // editing here never starts from blank defaults and clobbers data saved from Profile.
  useEffect(() => {
    const hydrate = async () => {
      try {
        const res = await axios.get(`${API}/cv`, { headers: getAuthHeaders() });
        if (res.data && res.data.user_id) {
          setCv({
            name: res.data.name || user?.name || "", title: res.data.title || "",
            location: res.data.location || "", phone: res.data.phone || "",
            email: res.data.email || user?.email || "", linkedin: res.data.linkedin || "",
            summary: res.data.summary || "",
            experience: res.data.experience?.length ? res.data.experience : [{ ...EMPTY_EXP }],
            education: res.data.education?.length ? res.data.education : [{ ...EMPTY_EDU }],
            skills: res.data.skills || [], languages: res.data.languages || [],
            certifications: res.data.certifications || [], industries: res.data.industries || [],
            years_experience: res.data.years_experience || 0,
          });
          setSavedCV(res.data);
        }
      } catch { /* no CV saved yet — keep the blank starting defaults */ }
      finally { setHydrating(false); }
    };
    hydrate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Scroll to top on entry and whenever the wizard step changes
  React.useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [step]);

  if (hydrating) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center" data-testid="cv-builder-loading">
        <Loader2 className="h-8 w-8 text-[#028CF8] animate-spin" />
      </div>
    );
  }

  const aiGenerate = async (sectionType, inputText, onSuccess) => {
    if (!inputText.trim()) { showToast(t("cv.addTextFirst")); return; }
    setAiLoading(true);
    try {
      const res = await axios.post(`${API}/cv/generate-section`, { section_type: sectionType, input_text: inputText }, { headers: getAuthHeaders() });
      onSuccess(res.data.generated_text);
      showToast(t("cv.aiImproved"));
    } catch { showToast(t("errors.aiNotConfigured")); }
    finally { setAiLoading(false); }
  };

  const saveCV = async () => {
    setSaving(true);
    const isFirstSave = !savedCV;
    try {
      // Drop untouched blank placeholder rows before persisting so they never count
      // as real experience/education (shared doc with the Professional Profile tab).
      const payload = {
        ...cv,
        experience: cv.experience.filter((e) => e.company?.trim() || e.position?.trim()),
        education: cv.education.filter((e) => e.institution?.trim() || e.degree?.trim()),
      };
      const res = await axios.post(`${API}/cv`, payload, { headers: getAuthHeaders() });
      setSavedCV(res.data);
      showToast(t("cv.saved"));
      track(isFirstSave ? "cv_created" : "cv_updated");
      if (payload.experience.length && payload.education.length && payload.skills?.length) {
        track("cv_completed");
      }
    } catch { showToast(t("errors.unknownError")); }
    finally { setSaving(false); }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("cv_file", file);
      const res = await axios.post(`${API}/cv/upload-parse`, fd, { headers: { ...getAuthHeaders(), "Content-Type": "multipart/form-data" } });
      const parsed = res.data.cv;
      setCv((c) => ({ ...c, ...parsed }));
      showToast(t("cv.parsedOK"));
      setStep(6);
    } catch { showToast(t("cv.parseFailed")); }
    finally { setUploading(false); }
  };

  const downloadCvFile = async (format) => {
    try {
      const mime = format === "pdf" ? "application/pdf" : "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
      const res = await axios.get(`${API}/cv/download-${format}`, { headers: getAuthHeaders(), responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: mime }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `${(cv.name || "CV").replace(/\s+/g, "_")}.${format}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch { showToast(t("errors.unknownError")); }
  };
  const downloadPdf = () => downloadCvFile("pdf");
  const downloadDocx = () => downloadCvFile("docx");

  // ── Step renders ─────────────────────────────────────────────────────────────

  const stepContent = [
    // 0 — Personal Info
    <div key="personal" style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div><FieldLabel>{t("cv.fullName")} *</FieldLabel><Input value={cv.name} onChange={(v) => set("name", v)} placeholder={t("auth.namePlaceholder")} /></div>
        <div><FieldLabel>{t("cv.professionalTitle")}</FieldLabel><Input value={cv.title} onChange={(v) => set("title", v)} placeholder={t("cv.titlePlaceholder")} /></div>
        <div><FieldLabel>{t("auth.emailLabel")}</FieldLabel><Input value={cv.email} onChange={(v) => set("email", v)} placeholder={t("auth.emailPlaceholder")} /></div>
        <div><FieldLabel>{t("userDash.phoneLabel")}</FieldLabel><Input value={cv.phone} onChange={(v) => set("phone", v)} placeholder="+1 (555) 000-0000" /></div>
        <div><FieldLabel>{t("userDash.locationLabel")}</FieldLabel><Input value={cv.location} onChange={(v) => set("location", v)} placeholder="San Francisco, CA" /></div>
        <div><FieldLabel>{t("userDash.linkedinLabel")}</FieldLabel><Input value={cv.linkedin} onChange={(v) => set("linkedin", v)} placeholder="https://linkedin.com/in/..." /></div>
        <div><FieldLabel>{t("cv.yearsExp")}</FieldLabel><Input type="number" value={cv.years_experience} onChange={(v) => set("years_experience", Number(v))} placeholder="5" /></div>
      </div>
      {/* Upload existing CV */}
      <div style={{ marginTop: "8px", padding: "16px", background: "#F8FAFC", border: "1px dashed #E5E7EB", borderRadius: "12px" }}>
        <p style={{ fontSize: "13px", fontWeight: 600, color: "#64748B", marginBottom: "10px", fontFamily: "Poppins, sans-serif" }}>
          {t("cv.importCVHint")}
        </p>
        <label style={{ display: "inline-flex", alignItems: "center", gap: "8px", height: "38px", padding: "0 16px", background: "white", border: "1px solid #E5E7EB", borderRadius: "9px", fontSize: "13px", fontWeight: 600, color: "#028CF8", cursor: "pointer", fontFamily: "Poppins, sans-serif" }}>
          {uploading ? <Loader2 style={{ width: 15, height: 15 }} /> : <Upload style={{ width: 15, height: 15 }} />}
          {uploading ? t("cv.parsing") : t("cv.uploadCVBtn")}
          <input type="file" accept=".pdf,.doc,.docx" onChange={handleUpload} style={{ display: "none" }} />
        </label>
      </div>
    </div>,

    // 1 — Summary
    <div key="summary" style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
      <p style={{ fontSize: "13px", color: "#64748B", fontFamily: "Poppins, sans-serif" }}>
        {t("cv.summaryHint")}
      </p>
      <Textarea value={cv.summary} onChange={(v) => set("summary", v)} placeholder={t("cv.summaryPlaceholder")} rows={6} />
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <AIButton loading={aiLoading} onClick={() => aiGenerate("summary", cv.summary || cv.title + " " + cv.industries.join(", "), (text) => set("summary", text))} label={t("cv.generateAI")} />
      </div>
    </div>,

    // 2 — Experience
    <div key="experience" style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
      {cv.experience.map((exp, i) => (
        <div key={i} style={{ background: "#F8FAFC", border: "1px solid #E5E7EB", borderRadius: "12px", padding: "16px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
            <span style={{ fontSize: "13px", fontWeight: 700, color: "#0F172A", fontFamily: "Poppins, sans-serif" }}>{t("cv.stepExperience")} {i + 1}</span>
            {cv.experience.length > 1 && (
              <button onClick={() => set("experience", cv.experience.filter((_, j) => j !== i))} style={{ background: "none", border: "none", cursor: "pointer", color: "#94A3B8" }}>
                <Trash2 style={{ width: 15, height: 15 }} />
              </button>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><FieldLabel>{t("cv.company")}</FieldLabel><Input value={exp.company} onChange={(v) => { const a = [...cv.experience]; a[i] = { ...a[i], company: v }; set("experience", a); }} placeholder={t("cv.companyPlaceholder")} /></div>
            <div><FieldLabel>{t("cv.position")}</FieldLabel><Input value={exp.position} onChange={(v) => { const a = [...cv.experience]; a[i] = { ...a[i], position: v }; set("experience", a); }} placeholder={t("cv.positionPlaceholder")} /></div>
            <div><FieldLabel>{t("cv.startDate")}</FieldLabel><Input value={exp.start_date} onChange={(v) => { const a = [...cv.experience]; a[i] = { ...a[i], start_date: v }; set("experience", a); }} placeholder="Jan 2020" /></div>
            <div>
              <FieldLabel>{t("cv.endDate")}</FieldLabel>
              <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                <Input value={exp.current ? t("profProfile.present") : exp.end_date} onChange={(v) => { const a = [...cv.experience]; a[i] = { ...a[i], end_date: v }; set("experience", a); }} placeholder="Dec 2023" disabled={exp.current} />
                <label style={{ display: "flex", alignItems: "center", gap: "4px", fontSize: "12px", color: "#64748B", whiteSpace: "nowrap", cursor: "pointer", fontFamily: "Poppins, sans-serif" }}>
                  <input type="checkbox" checked={exp.current} onChange={(e) => { const a = [...cv.experience]; a[i] = { ...a[i], current: e.target.checked }; set("experience", a); }} />
                  {t("cv.currentJob")}
                </label>
              </div>
            </div>
          </div>
          <div style={{ marginTop: "10px" }}>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", justifyContent: "space-between", alignItems: "center", marginBottom: "6px" }}>
              <FieldLabel>{t("cv.responsibilities")}</FieldLabel>
              <AIButton loading={aiLoading} onClick={() => aiGenerate("experience", exp.description || exp.position, (t) => { const a = [...cv.experience]; a[i] = { ...a[i], description: t }; set("experience", a); })} label={t("cv.aiImprove")} />
            </div>
            <Textarea value={exp.description} onChange={(v) => { const a = [...cv.experience]; a[i] = { ...a[i], description: v }; set("experience", a); }} placeholder={t("cv.responsibilitiesPlaceholder")} rows={3} />
          </div>
        </div>
      ))}
      <button onClick={() => set("experience", [...cv.experience, { ...EMPTY_EXP }])}
        style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "13px", fontWeight: 600, color: "#028CF8", background: "none", border: "1px dashed #028CF8", borderRadius: "10px", padding: "10px 16px", cursor: "pointer", fontFamily: "Poppins, sans-serif" }}>
        <Plus style={{ width: 14, height: 14 }} /> {t("cv.addExperience")}
      </button>
    </div>,

    // 3 — Education
    <div key="education" style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
      {cv.education.map((edu, i) => (
        <div key={i} style={{ background: "#F8FAFC", border: "1px solid #E5E7EB", borderRadius: "12px", padding: "16px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "12px" }}>
            <span style={{ fontSize: "13px", fontWeight: 700, color: "#0F172A", fontFamily: "Poppins, sans-serif" }}>{t("cv.stepEducation")} {i + 1}</span>
            {cv.education.length > 1 && <button onClick={() => set("education", cv.education.filter((_, j) => j !== i))} style={{ background: "none", border: "none", cursor: "pointer", color: "#94A3B8" }}><Trash2 style={{ width: 15, height: 15 }} /></button>}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><FieldLabel>{t("cv.institution")}</FieldLabel><Input value={edu.institution} onChange={(v) => { const a = [...cv.education]; a[i] = { ...a[i], institution: v }; set("education", a); }} placeholder={t("cv.institutionPlaceholder")} /></div>
            <div><FieldLabel>{t("cv.degree")}</FieldLabel><Input value={edu.degree} onChange={(v) => { const a = [...cv.education]; a[i] = { ...a[i], degree: v }; set("education", a); }} placeholder={t("cv.degreePlaceholder")} /></div>
            <div><FieldLabel>{t("cv.field")}</FieldLabel><Input value={edu.field} onChange={(v) => { const a = [...cv.education]; a[i] = { ...a[i], field: v }; set("education", a); }} placeholder={t("cv.fieldPlaceholder")} /></div>
            <div><FieldLabel>{t("cv.year")}</FieldLabel><Input value={edu.year} onChange={(v) => { const a = [...cv.education]; a[i] = { ...a[i], year: v }; set("education", a); }} placeholder="2019" /></div>
          </div>
        </div>
      ))}
      <button onClick={() => set("education", [...cv.education, { ...EMPTY_EDU }])}
        style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "13px", fontWeight: 600, color: "#028CF8", background: "none", border: "1px dashed #028CF8", borderRadius: "10px", padding: "10px 16px", cursor: "pointer", fontFamily: "Poppins, sans-serif" }}>
        <Plus style={{ width: 14, height: 14 }} /> {t("cv.addEducation")}
      </button>
    </div>,

    // 4 — Skills
    <div key="skills" style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
      <div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
          <FieldLabel>{t("cv.skillsFieldLabel")}</FieldLabel>
          <AIButton loading={aiLoading} onClick={() => aiGenerate("skills", cv.experience.map((e) => e.position + " " + e.description).join(". "), (t) => set("skills", [...new Set([...cv.skills, ...t.split(",").map((s) => s.trim()).filter(Boolean)])]))} label={t("cv.suggestSkills")} />
        </div>
        <div style={{ border: "1px solid #E5E7EB", borderRadius: "10px", padding: "10px", minHeight: "100px", background: "white" }}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginBottom: "8px" }}>
            {cv.skills.map((s, i) => (
              <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: "4px", background: "#EFF6FF", color: "#028CF8", borderRadius: "999px", padding: "4px 10px", fontSize: "12px", fontWeight: 600, fontFamily: "Poppins, sans-serif" }}>
                {s} <button onClick={() => set("skills", cv.skills.filter((_, j) => j !== i))} style={{ background: "none", border: "none", cursor: "pointer", color: "#028CF8", padding: 0, lineHeight: 1 }}><X style={{ width: 11, height: 11 }} /></button>
              </span>
            ))}
          </div>
          <input
            placeholder={t("cv.skillsHint")}
            onKeyDown={(e) => { if (e.key === "Enter" && e.target.value.trim()) { set("skills", [...cv.skills, e.target.value.trim()]); e.target.value = ""; e.preventDefault(); } }}
            style={{ border: "none", outline: "none", fontSize: "16px", fontFamily: "Poppins, sans-serif", color: "#64748B", width: "100%" }}
          />
        </div>
      </div>
      <div>
        <FieldLabel>{t("cv.industries")}</FieldLabel>
        <div style={{ border: "1px solid #E5E7EB", borderRadius: "10px", padding: "10px", background: "white" }}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginBottom: "8px" }}>
            {cv.industries.map((s, i) => (
              <span key={i} data-testid={`cv-builder-industry-chip-${i}`} style={{ display: "inline-flex", alignItems: "center", gap: "4px", background: "#F1F5F9", color: "#374151", borderRadius: "999px", padding: "4px 10px", fontSize: "12px", fontWeight: 600, fontFamily: "Poppins, sans-serif" }}>
                {s} <button aria-label={t("profTab.delete")} data-testid={`cv-builder-industry-remove-${i}`} onClick={() => set("industries", cv.industries.filter((_, j) => j !== i))} style={{ background: "none", border: "none", cursor: "pointer", color: "#374151", padding: 0 }}><X style={{ width: 11, height: 11 }} /></button>
              </span>
            ))}
          </div>
          <input data-testid="cv-builder-industries-input" placeholder={t("cv.industriesHint")} onKeyDown={(e) => { if (e.key === "Enter" && e.target.value.trim()) { set("industries", [...cv.industries, e.target.value.trim()]); e.target.value = ""; e.preventDefault(); } }}
            style={{ border: "none", outline: "none", fontSize: "16px", fontFamily: "Poppins, sans-serif", color: "#64748B", width: "100%" }} />
        </div>
      </div>
    </div>,

    // 5 — Languages & Certifications
    <div key="languages" style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
      <div>
        <FieldLabel>{t("cv.languagesFieldLabel")}</FieldLabel>
        <div style={{ border: "1px solid #E5E7EB", borderRadius: "10px", padding: "10px", background: "white" }}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginBottom: "8px" }}>
            {cv.languages.map((s, i) => (
              <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: "4px", background: "#F0FDF4", color: "#15803D", borderRadius: "999px", padding: "4px 10px", fontSize: "12px", fontWeight: 600, fontFamily: "Poppins, sans-serif" }}>
                {s} <button onClick={() => set("languages", cv.languages.filter((_, j) => j !== i))} style={{ background: "none", border: "none", cursor: "pointer", color: "#15803D", padding: 0 }}><X style={{ width: 11, height: 11 }} /></button>
              </span>
            ))}
          </div>
          <input placeholder={t("cv.languagesHint")} onKeyDown={(e) => { if (e.key === "Enter" && e.target.value.trim()) { set("languages", [...cv.languages, e.target.value.trim()]); e.target.value = ""; e.preventDefault(); } }}
            style={{ border: "none", outline: "none", fontSize: "16px", fontFamily: "Poppins, sans-serif", color: "#64748B", width: "100%" }} />
        </div>
      </div>
      <div>
        <FieldLabel>{t("cv.certFieldLabel")}</FieldLabel>
        <div style={{ border: "1px solid #E5E7EB", borderRadius: "10px", padding: "10px", background: "white" }}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginBottom: "8px" }}>
            {cv.certifications.map((c, i) => (
              <span key={i} data-testid={`cv-builder-certification-chip-${i}`} style={{ display: "inline-flex", alignItems: "center", gap: "4px", background: "#FFFBEB", color: "#B45309", borderRadius: "999px", padding: "4px 10px", fontSize: "12px", fontWeight: 600, fontFamily: "Poppins, sans-serif" }}>
                {c.name || c} <button aria-label={t("profTab.delete")} data-testid={`cv-builder-certification-remove-${i}`} onClick={() => set("certifications", cv.certifications.filter((_, j) => j !== i))} style={{ background: "none", border: "none", cursor: "pointer", color: "#B45309", padding: 0 }}><X style={{ width: 11, height: 11 }} /></button>
              </span>
            ))}
          </div>
          <input data-testid="cv-builder-certifications-input" placeholder={t("cv.certHint")} onKeyDown={(e) => { if (e.key === "Enter" && e.target.value.trim()) { set("certifications", [...cv.certifications, { name: e.target.value.trim(), issuer: "", issue_date: "", expiry_date: "", no_expiry: true, credential_id: "", credential_url: "" }]); e.target.value = ""; e.preventDefault(); } }}
            style={{ border: "none", outline: "none", fontSize: "16px", fontFamily: "Poppins, sans-serif", color: "#64748B", width: "100%" }} />
        </div>
        <p style={{ fontSize: "11px", color: "#94A3B8", fontFamily: "Poppins, sans-serif", marginTop: "6px" }}>{t("cv.certDetailHint")}</p>
      </div>
    </div>,

    // 6 — Preview & Save
    <div key="preview" style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
      {/* Score */}
      {savedCV && (
        <div style={{ display: "flex", alignItems: "center", gap: "24px", background: "#F8FAFC", border: "1px solid #E5E7EB", borderRadius: "16px", padding: "20px" }}>
          <CVScoreRing score={savedCV.cv_score || 0} label={t("cv.cvScore")} />
          <div style={{ flex: 1 }}>
            <p style={{ fontSize: "14px", fontWeight: 700, color: "#0F172A", fontFamily: "Poppins, sans-serif", marginBottom: "6px" }}>{t("cv.cvStrength")}: {savedCV.cv_score}/100</p>
            {savedCV.suggestions?.length > 0 && (
              <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
                {savedCV.suggestions.map((s, i) => (
                  <li key={i} style={{ fontSize: "12px", color: "#64748B", fontFamily: "Poppins, sans-serif", display: "flex", alignItems: "center", gap: "6px", marginBottom: "3px" }}>
                    <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#64748B", flexShrink: 0 }} /> {t(`cvSuggestions.${s}`)}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
      {/* CV Summary */}
      <div style={{ background: "white", border: "1px solid #E5E7EB", borderRadius: "16px", padding: "24px" }}>
        <h2 style={{ fontSize: "22px", fontWeight: 700, color: "#0F172A", fontFamily: "Poppins, sans-serif", marginBottom: "4px" }}>{cv.name}</h2>
        {cv.title && <p style={{ fontSize: "15px", fontWeight: 600, color: "#028CF8", fontFamily: "Poppins, sans-serif", marginBottom: "8px" }}>{cv.title}</p>}
        <p style={{ fontSize: "12px", color: "#64748B", fontFamily: "Poppins, sans-serif", marginBottom: "16px" }}>
          {[cv.email, cv.phone, cv.location, cv.linkedin].filter(Boolean).join("  ·  ")}
        </p>
        {cv.summary && <p style={{ fontSize: "13px", color: "#374151", fontFamily: "Poppins, sans-serif", lineHeight: "1.6", marginBottom: "16px" }}>{cv.summary}</p>}
        {cv.skills.length > 0 && <p style={{ fontSize: "13px", color: "#374151", fontFamily: "Poppins, sans-serif" }}><strong>{t("profProfile.skills")}:</strong> {cv.skills.join(", ")}</p>}
      </div>
      {/* Actions — primary "Guardar CV" lives in the fixed nav bar (desktop
          inline / mobile bottom bar) to avoid a duplicated Save CTA here */}
      {savedCV ? (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
          <button data-testid="cv-download-pdf-button" onClick={downloadPdf} style={{ display: "flex", alignItems: "center", gap: "8px", height: "44px", padding: "0 20px", background: "white", color: "#028CF8", borderRadius: "12px", fontSize: "14px", fontWeight: 600, fontFamily: "Poppins, sans-serif", border: "1.5px solid #028CF8", cursor: "pointer" }}>
            <Download style={{ width: 15, height: 15 }} /> {t("cv.downloadPDF")}
          </button>
          <button data-testid="cv-download-word-button" onClick={downloadDocx} style={{ display: "flex", alignItems: "center", gap: "8px", height: "44px", padding: "0 20px", background: "white", color: "#64748B", borderRadius: "12px", fontSize: "14px", fontWeight: 600, fontFamily: "Poppins, sans-serif", border: "1px solid #E5E7EB", cursor: "pointer" }}>
            <Download style={{ width: 15, height: 15 }} /> {t("cv.downloadWord")}
          </button>
          <button data-testid="cv-go-to-dashboard-button" onClick={() => navigate("/dashboard")} style={{ height: "44px", padding: "0 20px", background: "white", color: "#64748B", borderRadius: "12px", fontSize: "14px", fontWeight: 600, fontFamily: "Poppins, sans-serif", border: "1px solid #E5E7EB", cursor: "pointer" }}>
            {t("cv.goToDashboard")}
          </button>
        </div>
      ) : (
        <p style={{ fontSize: "13px", color: "#94A3B8", fontFamily: "Poppins, sans-serif" }} data-testid="cv-save-hint">{t("cv.saveHint")}</p>
      )}
    </div>,
  ];

  return (
    <div data-testid="cv-builder-page" className="min-h-screen bg-[#F8FAFC] pb-24 lg:pb-0">
      {/* Toast */}
      {toast && (
        <div style={{ position: "fixed", top: "80px", right: "24px", zIndex: 9999, background: "#028CF8", color: "white", padding: "12px 20px", borderRadius: "12px", fontSize: "13px", fontWeight: 600, fontFamily: "Poppins, sans-serif", boxShadow: "0 4px 14px rgba(15,23,42,0.25)" }}>
          {toast}
        </div>
      )}

      {/* Mobile compact contextual header — replaces the full navbar/hero on
          small screens; desktop keeps the hero below untouched. */}
      <div
        data-testid="cv-builder-mobile-header"
        className="lg:hidden sticky top-0 z-30 bg-white border-b border-slate-200 flex items-center gap-3 px-4"
        style={{ paddingTop: "calc(12px + env(safe-area-inset-top))", paddingBottom: "12px" }}
      >
        <button
          data-testid="cv-builder-back-button"
          onClick={() => navigate("/dashboard?tab=cv")}
          className="p-1.5 -ml-1.5 rounded-lg text-slate-600 hover:bg-slate-100 transition-colors"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
        <h1 className="font-poppins text-base font-bold text-slate-900">{t("cv.builderTitle")}</h1>
      </div>

      {/* Hero header — desktop only */}
      <section className="hero-infocity py-10 hidden lg:block">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <p className="text-xs font-semibold text-[#F55FA5] uppercase tracking-widest mb-2 font-poppins">{t("cv.aiPowered")}</p>
          <h1 className="font-poppins text-3xl md:text-4xl font-bold text-white mb-1">{t("cv.builderTitle")}</h1>
          <p style={{ color: "rgba(255,255,255,0.85)", fontSize: "15px", fontFamily: "Poppins, sans-serif" }}>
            {t("cv.builderSubtitle")}
          </p>
        </div>
      </section>

      {/* Mobile compact stepper — "Paso X de 7 · Label" + progress + drawer trigger */}
      <div data-testid="cv-builder-mobile-stepper" className="lg:hidden px-4 pt-4 pb-1">
        <div className="flex items-center justify-between gap-3 mb-2">
          <p className="text-sm font-bold text-slate-900 font-poppins truncate">
            {t("cv.stepCounterPrefix")} {step + 1} {t("cv.stepCounterOf")} {STEPS.length} · {STEPS[step].label}
          </p>
          <button
            data-testid="cv-builder-view-steps-button"
            onClick={() => setDrawerOpen(true)}
            className="flex items-center gap-1 text-xs font-semibold text-[#028CF8] flex-shrink-0 font-poppins"
          >
            {t("cv.viewStages")} <ChevronDown className="h-3.5 w-3.5" />
          </button>
        </div>
        <div style={{ height: "4px", background: "#F1F5F9", borderRadius: "999px" }}>
          <div style={{ height: "100%", width: `${((step + 1) / STEPS.length) * 100}%`, background: "#028CF8", borderRadius: "999px", transition: "width 0.3s ease" }} />
        </div>
      </div>

      <div className="container mx-auto px-4 md:px-6 lg:px-8 py-4 lg:py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">

          {/* Sidebar — Step navigation, desktop only (mobile uses the "Ver etapas" drawer) */}
          <div className="hidden lg:block lg:col-span-1">
            <div style={{ background: "white", border: "1px solid #E5E7EB", borderRadius: "16px", padding: "8px", boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>
              {STEPS.map(({ id, label, icon: Icon }, i) => {
                const done = i < step;
                const active = i === step;
                return (
                  <button key={id} onClick={() => setStep(i)}
                    style={{ width: "100%", display: "flex", alignItems: "center", gap: "10px", padding: "11px 14px", borderRadius: "10px", border: "none", cursor: "pointer", textAlign: "left", background: active ? "#EFF6FF" : "transparent", transition: "all 0.15s" }}>
                    <div style={{ width: 28, height: 28, borderRadius: "8px", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", background: active ? "#028CF8" : done ? "#10B981" : "#F8FAFC", border: `1px solid ${active ? "#028CF8" : done ? "#10B981" : "#E5E7EB"}` }}>
                      {done ? <CheckCircle style={{ width: 14, height: 14, color: "white" }} /> : <Icon style={{ width: 13, height: 13, color: active ? "white" : "#94A3B8" }} />}
                    </div>
                    <span style={{ fontSize: "13px", fontWeight: active ? 700 : 500, color: active ? "#028CF8" : done ? "#10B981" : "#64748B", fontFamily: "Poppins, sans-serif" }}>
                      {label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Main content */}
          <div className="lg:col-span-3">
            <div style={{ background: "white", border: "1px solid #E5E7EB", borderRadius: "16px", padding: "20px" }} className="lg:p-7">
              <div className="hidden lg:block" style={{ marginBottom: "24px" }}>
                <h2 style={{ fontSize: "20px", fontWeight: 700, color: "#0F172A", fontFamily: "Poppins, sans-serif", marginBottom: "4px" }}>
                  {STEPS[step].label}
                </h2>
                <div style={{ height: "4px", background: "#F1F5F9", borderRadius: "999px" }}>
                  <div style={{ height: "100%", width: `${((step + 1) / STEPS.length) * 100}%`, background: "#028CF8", borderRadius: "999px", transition: "width 0.3s ease" }} />
                </div>
              </div>

              {stepContent[step]}

              <div className="flex items-start gap-2 mt-4 pt-4 border-t border-slate-100" data-testid="cv-builder-privacy-hint">
                <ShieldCheck className="h-4 w-4 text-slate-400 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-slate-600 leading-relaxed">{t("cv.privacyHint")}</p>
              </div>

              {/* Navigation — desktop inline (mobile uses the fixed bottom bar) */}
              <div className="hidden lg:flex" style={{ justifyContent: "space-between", marginTop: "28px", paddingTop: "20px", borderTop: "1px solid #F1F5F9" }}>
                <button onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0}
                  style={{ display: "flex", alignItems: "center", gap: "6px", height: "40px", padding: "0 18px", background: "white", color: step === 0 ? "#CBD5E1" : "#64748B", border: `1px solid ${step === 0 ? "#E5E7EB" : "#CBD5E1"}`, borderRadius: "10px", fontSize: "13px", fontWeight: 600, fontFamily: "Poppins, sans-serif", cursor: step === 0 ? "not-allowed" : "pointer" }}>
                  <ChevronLeft style={{ width: 15, height: 15 }} /> {t("cv.previous")}
                </button>
                {step < STEPS.length - 1 ? (
                  <button onClick={() => setStep((s) => Math.min(STEPS.length - 1, s + 1))}
                    style={{ display: "flex", alignItems: "center", gap: "6px", height: "40px", padding: "0 20px", background: "#028CF8", color: "white", border: "none", borderRadius: "10px", fontSize: "13px", fontWeight: 600, fontFamily: "Poppins, sans-serif", cursor: "pointer" }}>
                    {t("cv.next")} <ChevronRight style={{ width: 15, height: 15 }} />
                  </button>
                ) : (
                  <button onClick={saveCV} disabled={saving}
                    style={{ display: "flex", alignItems: "center", gap: "6px", height: "40px", padding: "0 20px", background: "#028CF8", color: "white", border: "none", borderRadius: "10px", fontSize: "13px", fontWeight: 600, fontFamily: "Poppins, sans-serif", cursor: "pointer", opacity: saving ? 0.7 : 1 }}>
                    {saving ? <Loader2 style={{ width: 14, height: 14 }} /> : <CheckCircle style={{ width: 14, height: 14 }} />}
                    {saving ? t("cv.saving") : t("cv.saveCV")}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile fixed bottom bar — keeps Anterior/Siguiente always within reach */}
      <div
        data-testid="cv-builder-mobile-nav"
        className="lg:hidden fixed bottom-0 inset-x-0 z-30 bg-white border-t border-slate-200 flex items-center gap-3 px-4"
        style={{ paddingTop: "10px", paddingBottom: "calc(10px + env(safe-area-inset-bottom))" }}
      >
        <button
          data-testid="cv-builder-prev-button-mobile"
          onClick={() => setStep((s) => Math.max(0, s - 1))}
          disabled={step === 0}
          style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: "6px", height: "44px", background: "white", color: step === 0 ? "#CBD5E1" : "#64748B", border: `1px solid ${step === 0 ? "#E5E7EB" : "#CBD5E1"}`, borderRadius: "10px", fontSize: "13px", fontWeight: 600, fontFamily: "Poppins, sans-serif", cursor: step === 0 ? "not-allowed" : "pointer" }}
        >
          <ChevronLeft style={{ width: 15, height: 15 }} /> {t("cv.previous")}
        </button>
        {step < STEPS.length - 1 ? (
          <button
            data-testid="cv-builder-next-button-mobile"
            onClick={() => setStep((s) => Math.min(STEPS.length - 1, s + 1))}
            style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: "6px", height: "44px", background: "#028CF8", color: "white", border: "none", borderRadius: "10px", fontSize: "13px", fontWeight: 600, fontFamily: "Poppins, sans-serif", cursor: "pointer" }}
          >
            {t("cv.next")} <ChevronRight style={{ width: 15, height: 15 }} />
          </button>
        ) : (
          <button
            data-testid="cv-builder-save-button-mobile"
            onClick={saveCV}
            disabled={saving}
            style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: "6px", height: "44px", background: "#028CF8", color: "white", border: "none", borderRadius: "10px", fontSize: "13px", fontWeight: 600, fontFamily: "Poppins, sans-serif", cursor: "pointer", opacity: saving ? 0.7 : 1 }}
          >
            {saving ? <Loader2 style={{ width: 14, height: 14 }} /> : <CheckCircle style={{ width: 14, height: 14 }} />}
            {saving ? t("cv.saving") : t("cv.saveCV")}
          </button>
        )}
      </div>

      {/* "Ver etapas" bottom sheet — mobile step picker */}
      <Drawer open={drawerOpen} onOpenChange={setDrawerOpen}>
        <DrawerContent data-testid="cv-builder-steps-drawer">
          <DrawerHeader>
            <DrawerTitle className="font-poppins">{t("cv.viewStages")}</DrawerTitle>
            <DrawerDescription className="sr-only">{t("cv.viewStages")}</DrawerDescription>
          </DrawerHeader>
          <div className="px-4 pb-8 space-y-1" style={{ paddingBottom: "calc(24px + env(safe-area-inset-bottom))" }}>
            {STEPS.map(({ id, label, icon: Icon }, i) => {
              const done = i < step;
              const active = i === step;
              return (
                <button
                  key={id}
                  data-testid={`cv-builder-drawer-step-${id}`}
                  onClick={() => { setStep(i); setDrawerOpen(false); }}
                  className="w-full flex items-center gap-3 px-3 py-3 rounded-xl"
                  style={{ background: active ? "#EFF6FF" : "transparent" }}
                >
                  <div style={{ width: 30, height: 30, borderRadius: "8px", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", background: active ? "#028CF8" : done ? "#10B981" : "#F8FAFC", border: `1px solid ${active ? "#028CF8" : done ? "#10B981" : "#E5E7EB"}` }}>
                    {done ? <CheckCircle style={{ width: 15, height: 15, color: "white" }} /> : <Icon style={{ width: 14, height: 14, color: active ? "white" : "#94A3B8" }} />}
                  </div>
                  <span style={{ fontSize: "14px", fontWeight: active ? 700 : 500, color: active ? "#028CF8" : done ? "#10B981" : "#374151", fontFamily: "Poppins, sans-serif" }}>
                    {label}
                  </span>
                </button>
              );
            })}
          </div>
        </DrawerContent>
      </Drawer>
    </div>
  );
}
